import React, { useState, useEffect } from 'react'
import EventModal from 'components/utility/modals/EventModal'
import axios from 'axios'
import clsx from 'clsx'

const MODAL_ID = 'issue-final-decision'

// Jurisdiction-neutral defaults. The rounds plugin builds the canonical default
// server-side and a jurisdiction plugin (e.g. NY 1120-B) overrides copy via the
// `rounds.decision_modal_content` filter, so this component never hardcodes any
// jurisdiction-specific language (timers, "consent to sale", co-op law, etc.).
// These defaults only apply when the `content` prop is omitted entirely.
const DEFAULT_CONTENT = {
  icon: 'fat fa-paper-plane',
  titles: { issue: 'issue decision', confirm: 'confirm decision', sent: 'decision sent' },
  notifyText: 'Sending this decision will stop the timer and immediately notify the applicant team.',
  decisionLabel: 'Decision:',
  outcomeLabels: {
    granted_unconditionally: 'granted unconditionally',
    granted_with_conditions: 'granted subject to stated conditions',
    denied:                  'denied'
  },
  conditionsHelpText: '',
  otherPlaceholder: 'enter conditions here (max 500 characters)',
  confirmLabel: 'I confirm this decision has been agreed upon by the board.',
  sentText: 'The decision was sent to the applicant team.'
}

// Merge the injected content over the defaults (one level of nesting for the
// object-valued keys), so a partial override still yields a complete config.
function resolveContent(content) {
  return {
    ...DEFAULT_CONTENT,
    ...content,
    titles:        { ...DEFAULT_CONTENT.titles, ...(content?.titles) },
    outcomeLabels: { ...DEFAULT_CONTENT.outcomeLabels, ...(content?.outcomeLabels) }
  }
}


function StepIssue({ content, outcome, setOutcome, conditionsText, setConditionsText, onNext, onCancel, recipients }) {
  const conditionsValid = outcome !== 'granted_with_conditions' || !!conditionsText.trim()

  const nextEnabled = !!outcome && conditionsValid

  return (
    <div className="dome-d-flex dome-flex-column dome-gap12">
      <div className="dome-p1 dome-color-dark-grey">{content.notifyText}</div>


      <div className="dome-d-flex dome-flex-column dome-gap6" style={{padding: '12px 0', borderTop: '1px solid var(--disabled)', borderBottom: '1px solid var(--disabled)'}}>
        <div className="dome-p1 dome-text-w500 dome-text-lowercase">{content.decisionLabel}</div>
        {Object.entries(content.outcomeLabels).map(([value, label]) => (
          <div key={value} className="dome-d-flex dome-flex-column dome-gap6">
            <label className="dome-d-flex dome-align-center dome-gap6 dome-cursor-pointer">
              <input
                type="radio"
                name="decision-outcome"
                value={value}
                checked={outcome === value}
                onChange={() => setOutcome(value)}
                style={{margin: 0, accentColor: 'var(--darkest-grey)'}}
              />
              <span className="dome-p2">{label}</span>
            </label>
            {value === 'granted_with_conditions' && outcome === value && (
              <div className="dome-d-flex dome-flex-column dome-gap6 dome-padding12-0">
                {content.conditionsHelpText && (
                  <p className="dome-p3 dome-color-med-grey">{content.conditionsHelpText}</p>
                )}
                <textarea
                  className="issue-final-decision-modal__other"
                  maxLength={500}
                  placeholder={content.otherPlaceholder}
                  value={conditionsText}
                  onChange={e => setConditionsText(e.target.value)}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="dome-d-flex dome-justify-between dome-align-center">
        <button
          type="button"
          className="dome-btn dome-btn-link dome-btn-link-cancel dome-btn-link-med-grey"
          onClick={onCancel}
        >
          cancel
        </button>
        <button
          type="button"
          className={clsx("dome-btn dome-btn-base", nextEnabled ? 'dome-btn-dark-green' : 'dome-btn-disabled')}
          disabled={!nextEnabled}
          onClick={onNext}
        >
          next
        </button>
      </div>
    </div>
  )
}

function StepConfirm({ content, outcome, conditionsText, onBack, onSubmit, submitting, recipients }) {
  const [confirmed, setConfirmed] = useState(false)

  const hasConditions = outcome === 'granted_with_conditions' && !!conditionsText.trim()

  return (
    <div className="dome-d-flex dome-flex-column dome-gap12">
      <div className="dome-p1 dome-color-dark-grey">{content.notifyText}</div>


      <div style={{padding: '12px 0', borderTop: '1px solid var(--disabled)', borderBottom: '1px solid var(--disabled)'}}>
        <div className="dome-p1 dome-text-w500 dome-text-lowercase">{content.decisionLabel}</div>
        <p className="dome-p2">{content.outcomeLabels[outcome]}{hasConditions ? ':' : ''}</p>
        {hasConditions && (
          <ul className="dome-p2" style={{ paddingLeft: 24, margin: '4px 0 0' }}>
            <li>{conditionsText.trim()}</li>
          </ul>
        )}
      </div>

      <div className="dome-d-flex dome-flex-column dome-gap18" style={{marginTop: 6}}>
        <label className="dome-d-flex dome-align-start dome-gap6 dome-cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={() => setConfirmed(v => !v)}
            style={{ marginTop: 3, accentColor: 'var(--darkest-grey)' }}
          />
          <span className="dome-p2">{content.confirmLabel}</span>
        </label>
        <div className="dome-d-flex dome-justify-between dome-align-center">
          <button
            type="button"
            className="dome-btn dome-btn-link dome-btn-link-back"
            onClick={onBack}
            disabled={submitting}
          >
            back
          </button>
          <button
            type="button"
            className={clsx("dome-btn dome-btn-base", (!confirmed || submitting) ? 'dome-btn-disabled' : 'dome-btn-aqua-stroke')}
            disabled={!confirmed || submitting}
            onClick={onSubmit}
          >
            {submitting ? 'sending…' : 'send decision'}
          </button>
        </div>
      </div>
    </div>
  )
}

function StepSuccess({ content, recipients, statusUrl }) {
  return (
    <div className="dome-d-flex dome-flex-column dome-gap12">
      <p className="dome-p2">{content.sentText}</p>

      <div className="dome-d-flex dome-justify-between dome-align-center">
        <button
          type="button"
          className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-cancel"
          onClick={() => window.location.reload()}
        >
          close
        </button>
        <a href={statusUrl} className="dome-link dome-is-button dome-link-aqua-stroke">
          go to status
        </a>
      </div>
    </div>
  )
}

export default function IssueFinalDecisionModal({ packageId, onSuccess, recipients, content }) {
  const [step, setStep] = useState(1)
  const [outcome, setOutcome] = useState('')
  const [conditionsText, setConditionsText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [previewRecipients, setPreviewRecipients] = useState(null)

  const c = resolveContent(content)

  useEffect(() => {
    axios.get(`/api/packages/${packageId}/rounds/decision_recipients`)
      .then(response => setPreviewRecipients(response.data.recipients))
      .catch(e => console.warn('IssueFinalDecisionModal: failed to load recipients preview', e))
  }, [packageId])

  function resetAndClose() {
    window.dispatchEvent(new Event(`closeModal-${MODAL_ID}`))
    setTimeout(() => {
      setStep(1)
      setOutcome('')
      setConditionsText('')
      setError(null)
    }, 300)
  }

  async function handleSubmit() {
    setSubmitting(true)
    setError(null)

    const payload = { outcome }
    if (outcome === 'granted_with_conditions') {
      payload.conditions = { other: conditionsText.trim() }
    }

    try {
      const response = await axios.post(
        `/api/packages/${packageId}/rounds/issue_decision`,
        payload
      )
      onSuccess(response.data.recipients)
      setStep(3)
    } catch (e) {
      setError(e.response?.data?.error || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const titles = { 1: c.titles.issue, 2: c.titles.confirm, 3: c.titles.sent }

  const headerContent = (
    <div className="dome-d-flex dome-flex-column dome-gap12" style={{ textAlign: 'left', alignItems: 'flex-start', marginBottom: 18 }}>
      <i className={c.icon} style={{ fontSize: 32, color: 'var(--dark-green)' }} />
      <div className="dome-h2" style={{ textAlign: 'left', color: 'var(--dark-green)' }}>{titles[step]}</div>
    </div>
  )

  const bodyContent = (
    <div className="issue-final-decision-modal" style={{ textAlign: 'left' }}>
      {error && (
        <div className="dome-alert dome-alert-danger dome-margin-bottom12">{error}</div>
      )}
      {step === 1 && (
        <StepIssue
          content={c}
          outcome={outcome}
          setOutcome={setOutcome}
          conditionsText={conditionsText}
          setConditionsText={setConditionsText}
          onNext={() => setStep(2)}
          onCancel={resetAndClose}
          recipients={previewRecipients}
        />
      )}
      {step === 2 && (
        <StepConfirm
          content={c}
          outcome={outcome}
          conditionsText={conditionsText}
          onBack={() => setStep(1)}
          onSubmit={handleSubmit}
          submitting={submitting}
          recipients={previewRecipients}
        />
      )}
      {step === 3 && (
        <StepSuccess content={c} recipients={recipients} statusUrl={`/packages/${packageId}/status`} />
      )}
    </div>
  )

  return (
    <EventModal
      modalId={MODAL_ID}
      headerContent={headerContent}
      bodyContent={bodyContent}
      footerContent={null}
      afterClose={resetAndClose}
      modalStyles={{ width: 500, padding: '36px 60px 24px' }}
    />
  )
}
